import React from 'react';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { useFullscreenLandscape } from '@/hooks/useFullscreenLandscape';

/**
 * Overlay for mobile game screens:
 * 1. Shows a "Rotate your device" prompt when in portrait mode
 * 2. Shows a small fullscreen button in the top-right when not in fullscreen
 */
const MobileGameOverlay: React.FC = () => {
  const isTouchDevice = useIsTouchDevice();
  const { isFullscreen, isLandscape, supportsFullscreen, enterFullscreen, exitFullscreen } =
    useFullscreenLandscape();

  if (!isTouchDevice) return null;

  return (
    <>
      {/* Portrait mode blocker — covers everything and asks user to rotate */}
      {!isLandscape && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 24,
            color: '#fff',
            padding: 32,
            textAlign: 'center',
          }}
        >
          {/* Rotating phone icon */}
          <div
            style={{
              fontSize: '4rem',
              animation: 'rotatePhone 2s ease-in-out infinite',
            }}
          >
            📱
          </div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>
            Rotate Your Device
          </h2>
          <p style={{ margin: 0, fontSize: '1rem', opacity: 0.8, maxWidth: 280 }}>
            This game is best played in landscape mode. Please turn your phone sideways.
          </p>
          {supportsFullscreen && !isFullscreen && (
            <button
              onClick={enterFullscreen}
              style={{
                marginTop: 8,
                padding: '14px 28px',
                fontSize: '1rem',
                fontWeight: 700,
                borderRadius: 12,
                border: '2px solid rgba(255,255,255,0.3)',
                background: 'rgba(255,255,255,0.15)',
                color: '#fff',
                cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                touchAction: 'manipulation',
              }}
            >
              🖥️ Enter Fullscreen
            </button>
          )}
          <style>{`
            @keyframes rotatePhone {
              0%, 100% { transform: rotate(0deg); }
              25% { transform: rotate(-90deg); }
              50%, 75% { transform: rotate(-90deg); }
            }
          `}</style>
        </div>
      )}

      {/* Fullscreen toggle button — shown in landscape when not fullscreen */}
      {isLandscape && supportsFullscreen && (
        <button
          onClick={isFullscreen ? exitFullscreen : enterFullscreen}
          style={{
            position: 'fixed',
            top: 8,
            right: 8,
            zIndex: 1001,
            width: 36,
            height: 36,
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.25)',
            background: 'rgba(0,0,0,0.4)',
            color: '#fff',
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            touchAction: 'manipulation',
            backdropFilter: 'blur(4px)',
            padding: 0,
          }}
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? '⊡' : '⛶'}
        </button>
      )}
    </>
  );
};

export default MobileGameOverlay;
